import { FC, Fragment, useEffect, useRef } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  enteredPercentageViewRules,
  numberCalculatedViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import TableSectionValuesPerForecast from '@app/modules/projects/outputs/components/TableSectionValuesPerForecast';
import { selectSortedCasesStructure } from '@core/store/project-slice-selectors';
import { cloneToMutableData } from '@app/shared/helpers';
import { useAppDispatch, useAppSelector } from '@core/hooks/redux-hooks';
import { Navigate, generatePath, useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { formConfigBase } from '@app/shared/constants/form-config-base';
import { CaseDto } from '@app/shared/models/contracts/project-dto';
import * as projectActions from '@core/store/project-slice';
import { CellValueTheme } from '@app/shared/components/cell-value/cell-value-theme';
import { CalcMethod } from '@app/shared/models/contracts/enums/shared-enums';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { useLocale } from '@app/core/hooks/useLocale';
import { setCostOfEquitySelectedTab } from '@app/core/store/ui-values-slice';
import {
  getInstrumentCaseInitialDebtInstrument,
  getInstrumentCaseInitialFirstTranche,
  getInstrumentCaseInitialTranche,
  getInstrumentCaseYearDebtInstrument,
  getInstrumentCaseYearFirstTranche,
  getInstrumentCaseYearTranche,
  getInstrumentsCaseInitial,
  getInstrumentsCaseYear,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { selectCapitalStructure } from '@app/core/store/capital-structure-slice-selectors';
import { Pwerm2CalculationCostOfEquityInstrumentCaseYearDebtInstrumentDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { ExitMultipleWeightedEVSection2 } from '../components/ExitMultipleWeightedEVSection2';
import { InstrumentLevelDataHeader } from './InstrumentLevelDataHeader';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import { RootAppRoutes } from '@app/shared/enums/root-app-routes';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';

interface GetRefProp {
  getRef?: (ref: HTMLTableElement) => void;
}

export const InstrumentLevelData2: FC<GetRefProp> = ({ getRef }): JSX.Element => {
  const { instrumentId: currentInstrumentId } = useParams();
  const dispatch = useAppDispatch();
  const { l } = useLocale();
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    if (tableRef.current && getRef) {
      getRef(tableRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const casesStructure = useAppSelector(selectSortedCasesStructure);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const project = useAppSelector((state) => state.project.projectDraft);
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const instrumentExists = currentInstrumentId
    ? Object.keys(capitalStructure.instrumentDefinitions).includes(currentInstrumentId)
    : false;

  const currentInstrumentDefinition = {
    instrumentId: currentInstrumentId ?? '',
    ...capitalStructure.instrumentDefinitions[currentInstrumentId!],
  };

  const getDebtInstruments =
    (): Dictionary<Pwerm2CalculationCostOfEquityInstrumentCaseYearDebtInstrumentDto> => {
      // get the instruments that appear at some point across the years within cases
      // because of ERF, an instrument might not appear in the initial data or within certain years
      const allDebtInstruments = Object.values(
        results.costOfEquity.instruments[currentInstrumentDefinition!.instrumentId].cases
      ).flatMap((i) =>
        Object.values(i.years).flatMap((y) => Object.entries(y?.debtInstruments ?? {}))
      );

      return allDebtInstruments.reduce(
        (
          acc: Dictionary<Pwerm2CalculationCostOfEquityInstrumentCaseYearDebtInstrumentDto>,
          [instrumentId, data]
        ) => {
          acc[instrumentId] = data;
          return acc;
        },
        {}
      );
    };

  const columnsCount =
    casesStructure.length + casesStructure.flatMap((caseItem) => caseItem.forecasts).length + 1;

  const formMethods = useForm<{ cases: CaseDto[] }>({
    ...formConfigBase,
    defaultValues: { cases: casesStructure },
  });

  const { handleSubmit, reset, getValues, trigger } = formMethods;

  useTriggerEffect(trigger);

  const submitData = async () => {
    await dispatch(
      projectActions.updateProjectDraft({
        project: {
          ...project,
          pwermInput: {
            ...project.pwermInput,
            cases: [...cloneToMutableData(getValues().cases)],
          },
        },
      })
    ).unwrap();
  };

  useEffect(() => {
    reset({ cases: [...cloneToMutableData(getValues().cases)] });
  }, [reset, currentInstrumentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentInstrumentIdRef = useRef(currentInstrumentId);

  useEffect(() => {
    currentInstrumentIdRef.current = currentInstrumentId;
  }, [currentInstrumentId]);

  useEffect(() => {
    return () => {
      dispatch(setCostOfEquitySelectedTab(currentInstrumentIdRef.current));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredSlnAndPrefSharesInstruments = Object.entries(getDebtInstruments()).map(
    ([instrumentId, data]) => ({
      instrumentId,
      ...data,
    })
  );
  const sortedFilteredSlnAndPrefSharesInstruments = useInstrumentSort(
    filteredSlnAndPrefSharesInstruments
  );

  if (project.details.calcMethod === CalcMethod.OPM) {
    const newPath = generatePath(`../../${RouteConstants.ProjectDetails}`);
    return <Navigate to={newPath} />;
  }

  if (!instrumentExists) {
    return <Navigate to={RootAppRoutes.NotFound} />;
  }

  return (
    <div className="main-container-padding-remove">
      <FormProvider {...formMethods}>
        <form onBlur={handleSubmit(submitData, submitData)}>
          <table
            ref={tableRef}
            className="table-primary table-primary--framed table-primary--zebra table-primary--title-column-nowrap table-primary--sticky-first-column">
            <InstrumentLevelDataHeader />
            <tbody>
              <ExitMultipleWeightedEVSection2 />
              <tr>
                <th className="table-primary__cell--header">
                  {l('_IndebtednessRelativeToInstrument')}
                </th>
                <th className="table-primary__cell--header" colSpan={columnsCount - 1} />
              </tr>
              <TableSectionValuesPerForecast
                title={l('_NetDebt')}
                renderSpacer={false}
                renderCell={({ forecast, caseItem }) => (
                  <CellValue
                    {...amountRules}
                    value={getInstrumentsCaseYear(
                      results,
                      currentInstrumentId!,
                      caseItem.caseId,
                      forecast.forecastId,
                      (year) => year?.totalNetDebt
                    )}
                  />
                )}
                renderAdditionalCell={({ caseItem }) => (
                  <CellValue
                    {...amountRules}
                    value={getInstrumentsCaseInitial(
                      results,
                      currentInstrumentId!,
                      caseItem.caseId,
                      (year) => year?.totalNetDebt
                    )}
                    theme={CellValueTheme.Tertiary}
                  />
                )}
              />
              {sortedFilteredSlnAndPrefSharesInstruments.map((instrument) => (
                <TableSectionValuesPerForecast
                  renderSpacer={false}
                  key={instrument.instrumentId}
                  title={instrument.instrumentNarrative}
                  renderCell={({ caseItem, forecast }) => (
                    <CellValue
                      {...amountRules}
                      value={getInstrumentCaseYearDebtInstrument(
                        results,
                        currentInstrumentId!,
                        caseItem.caseId,
                        forecast.forecastId,
                        instrument.instrumentId,
                        (year) => year?.valueCarriedForward
                      )}
                    />
                  )}
                  renderAdditionalCell={({ caseItem }) => (
                    <CellValue
                      {...amountRules}
                      value={getInstrumentCaseInitialDebtInstrument(
                        results,
                        currentInstrumentId!,
                        caseItem.caseId,
                        instrument.instrumentId,
                        (year) => year?.valueCarriedForward
                      )}
                      theme={CellValueTheme.Tertiary}
                    />
                  )}
                />
              ))}
              <TableSectionValuesPerForecast
                title={l('_TotalIndebtednessRelativeToInstrument')}
                renderSpacer={false}
                renderCell={({ caseItem, forecast }) => (
                  <CellValue
                    {...amountRules}
                    value={getInstrumentsCaseYear(
                      results,
                      currentInstrumentId!,
                      caseItem.caseId,
                      forecast.forecastId,
                      (year) => year?.totalInstrumentIndebtedness
                    )}
                  />
                )}
                renderAdditionalCell={({ caseItem }) => (
                  <CellValue
                    {...amountRules}
                    value={getInstrumentsCaseInitial(
                      results,
                      currentInstrumentId!,
                      caseItem.caseId,
                      (year) => year?.totalInstrumentIndebtedness
                    )}
                    theme={CellValueTheme.Tertiary}
                  />
                )}
                additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                additionalCellClassNames="table-primary__cell--section-start-separator"
              />
              {(currentInstrumentDefinition?.payoutLogic?.length ?? 0) === 0 && (
                <>
                  <TableSectionValuesPerForecast
                    title={l('_SpotGearing')}
                    renderSpacer={false}
                    renderAdditionalCell={({ caseItem }) => (
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          getInstrumentCaseInitialFirstTranche(
                            results,
                            currentInstrumentId!,
                            caseItem.caseId,
                            (tranche) => tranche.spotGearing
                          )
                        )}
                        theme={CellValueTheme.Tertiary}
                      />
                    )}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          getInstrumentCaseYearFirstTranche(
                            results,
                            currentInstrumentId!,
                            caseItem.caseId,
                            forecast.forecastId,
                            (tranche) => tranche.spotGearing
                          )
                        )}
                      />
                    )}
                  />
                  <TableSectionValuesPerForecast
                    title={l('_AverageGearing')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          getInstrumentCaseYearFirstTranche(
                            results,
                            currentInstrumentId!,
                            caseItem.caseId,
                            forecast.forecastId,
                            (tranche) => tranche.averageGearing
                          )
                        )}
                      />
                    )}
                  />
                  <TableSectionValuesPerForecast
                    title={l('_DebtBeta')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...numberCalculatedViewRules}
                        normalize
                        value={getInstrumentCaseYearFirstTranche(
                          results,
                          currentInstrumentId!,
                          caseItem.caseId,
                          forecast.forecastId,
                          (tranche) => tranche.debtBeta
                        )}
                      />
                    )}
                  />
                  <TableSectionValuesPerForecast
                    title={l('_EquityBeta')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...numberCalculatedViewRules}
                        normalize
                        value={getInstrumentCaseYearFirstTranche(
                          results,
                          currentInstrumentId!,
                          caseItem.caseId,
                          forecast.forecastId,
                          (tranche) => tranche.equityBeta
                        )}
                      />
                    )}
                  />
                  <TableSectionValuesPerForecast
                    title={l('_CostOfEquitySpot')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          getInstrumentCaseYearFirstTranche(
                            results,
                            currentInstrumentId!,
                            caseItem.caseId,
                            forecast.forecastId,
                            (tranche) => tranche.spotCostOfEquity
                          )
                        )}
                      />
                    )}
                    additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                    additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                  />
                  <TableSectionValuesPerForecast
                    title={l('_DiscountFactorPoint')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...numberCalculatedViewRules}
                        normalize
                        value={getInstrumentCaseYearFirstTranche(
                          results,
                          currentInstrumentId!,
                          caseItem.caseId,
                          forecast.forecastId,
                          (tranche) => tranche.pointDiscountFactor
                        )}
                      />
                    )}
                  />
                  <TableSectionValuesPerForecast
                    title={l('_DiscountFactorCumulative')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...numberCalculatedViewRules}
                        normalize
                        value={getInstrumentCaseYearFirstTranche(
                          results,
                          currentInstrumentId!,
                          caseItem.caseId,
                          forecast.forecastId,
                          (tranche) => tranche.cumulativeDiscountFactor
                        )}
                      />
                    )}
                  />
                  <TableSectionValuesPerForecast
                    title={l('_CostOfEquityCumulative')}
                    renderSpacer={false}
                    renderCell={({ caseItem, forecast }) => (
                      <CellValue
                        {...enteredPercentageViewRules}
                        value={getCalculatedProjectValueInPercent(
                          getInstrumentCaseYearFirstTranche(
                            results,
                            currentInstrumentId!,
                            caseItem.caseId,
                            forecast.forecastId,
                            (tranche) => tranche.cumulativeCostOfEquity
                          )
                        )}
                      />
                    )}
                    additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                    additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                  />
                </>
              )}
              {(currentInstrumentDefinition?.payoutLogic?.length ?? 0) > 0 && (
                <>
                  {currentInstrumentDefinition?.payoutLogic?.map((tranche, index) => (
                    <Fragment key={index}>
                      <tr>
                        <th className="table-primary__cell--header table-primary__cell--header table-primary__cell--header-tertiary">
                          {l('_Tranche')} {index + 1}
                        </th>
                        <th
                          className="table-primary__cell--header table-primary__cell--header table-primary__cell--header-tertiary"
                          colSpan={columnsCount - 1}
                        />
                      </tr>
                      <TableSectionValuesPerForecast
                        title={l('_EffectiveHurdle')}
                        renderSpacer={false}
                        renderAdditionalCell={({ caseItem }) => (
                          <CellValue
                            {...amountRules}
                            value={getInstrumentCaseInitialTranche(
                              results,
                              currentInstrumentId!,
                              caseItem.caseId,
                              tranche.id,
                              (tranche) => tranche?.ordinaryEquityHurdle
                            )}
                            theme={CellValueTheme.Tertiary}
                          />
                        )}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...amountRules}
                            value={getInstrumentCaseYearTranche(
                              results,
                              currentInstrumentId!,
                              caseItem.caseId,
                              forecast.forecastId,
                              tranche.id,
                              (tranche) => tranche?.ordinaryEquityHurdle
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_SpotGearing')}
                        renderSpacer={false}
                        renderAdditionalCell={({ caseItem }) => (
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              getInstrumentCaseInitialTranche(
                                results,
                                currentInstrumentId!,
                                caseItem.caseId,
                                tranche.id,
                                (tranche) => tranche?.spotGearing
                              )
                            )}
                            theme={CellValueTheme.Tertiary}
                          />
                        )}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              getInstrumentCaseYearTranche(
                                results,
                                currentInstrumentId!,
                                caseItem.caseId,
                                forecast.forecastId,
                                tranche.id,
                                (tranche) => tranche?.spotGearing
                              )
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_AverageGearing')}
                        renderSpacer={false}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              getInstrumentCaseYearTranche(
                                results,
                                currentInstrumentId!,
                                caseItem.caseId,
                                forecast.forecastId,
                                tranche.id,
                                (tranche) => tranche?.averageGearing
                              )
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_DebtBeta')}
                        renderSpacer={false}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...numberCalculatedViewRules}
                            normalize
                            value={getInstrumentCaseYearTranche(
                              results,
                              currentInstrumentId!,
                              caseItem.caseId,
                              forecast.forecastId,
                              tranche.id,
                              (tranche) => tranche?.debtBeta
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_EquityBeta')}
                        renderSpacer={false}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...numberCalculatedViewRules}
                            normalize
                            value={getInstrumentCaseYearTranche(
                              results,
                              currentInstrumentId!,
                              caseItem.caseId,
                              forecast.forecastId,
                              tranche.id,
                              (tranche) => tranche?.equityBeta
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_CostOfEquitySpot')}
                        renderSpacer={false}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              getInstrumentCaseYearTranche(
                                results,
                                currentInstrumentId!,
                                caseItem.caseId,
                                forecast.forecastId,
                                tranche.id,
                                (tranche) => tranche?.spotCostOfEquity
                              )
                            )}
                          />
                        )}
                        additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                        additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                      />
                      <TableSectionValuesPerForecast
                        title={l('_DiscountFactorPoint')}
                        renderSpacer={false}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...numberCalculatedViewRules}
                            normalize
                            value={getInstrumentCaseYearTranche(
                              results,
                              currentInstrumentId!,
                              caseItem.caseId,
                              forecast.forecastId,
                              tranche.id,
                              (tranche) => tranche?.pointDiscountFactor
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_DiscountFactorCumulative')}
                        renderSpacer={false}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...numberCalculatedViewRules}
                            normalize
                            value={getInstrumentCaseYearTranche(
                              results,
                              currentInstrumentId!,
                              caseItem.caseId,
                              forecast.forecastId,
                              tranche.id,
                              (tranche) => tranche?.cumulativeDiscountFactor
                            )}
                          />
                        )}
                      />
                      <TableSectionValuesPerForecast
                        title={l('_CostOfEquityCumulative')}
                        renderCell={({ caseItem, forecast }) => (
                          <CellValue
                            {...enteredPercentageViewRules}
                            value={getCalculatedProjectValueInPercent(
                              getInstrumentCaseYearTranche(
                                results,
                                currentInstrumentId!,
                                caseItem.caseId,
                                forecast.forecastId,
                                tranche.id,
                                (tranche) => tranche?.cumulativeCostOfEquity
                              )
                            )}
                          />
                        )}
                        additionalTitleClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                        additionalCellClassNames="table-primary__cell--section-start-separator table-primary__cell--strong"
                      />
                    </Fragment>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </form>
      </FormProvider>
    </div>
  );
};
